import ReactSelect from "react-select";
import { useState, useMemo } from "react";
import { cn } from "@/utils";

type OptionType = { [key: string]: any };
export type OptionsType = Array<OptionType>;

interface Props {
  label?: string;
  name?: string;
  value?: OptionType;
  defaultValue?: OptionType;
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isRtl?: boolean;
  isSearchable?: boolean;
  options: OptionsType;
  className?: string;
  placeholder?: string;
  width?: string;
  height?: string;
  required?: boolean;
  error?: string;
  isRelativeMenu?: boolean;
  searchKeys?: string[];
  filterOption?: (option: OptionType, inputValue: string) => boolean;
  onChange?: (value: OptionType | null) => void;
  onMenuScrollToBottom?: ((event: WheelEvent | TouchEvent) => void) | undefined;
  onInputChange?: (inputValue: string) => void;
}

export const Select = ({
  label,
  name,
  value,
  defaultValue,
  isDisabled = false,
  isLoading = false,
  isClearable = false,
  isRtl = false,
  isSearchable = false,
  options,
  className,
  placeholder = "選択してください",
  searchKeys = ["label"],
  filterOption,
  width = "100%",
  height = "2.75rem",
  onChange,
  required,
  error,
  onMenuScrollToBottom,
  isRelativeMenu = false,
  onInputChange,
}: Props) => {
  const [searchValue, setSearchValue] = useState("");

  const defaultFilterOption = (
    option: OptionType,
    inputValue: string,
  ): boolean => {
    if (!inputValue) return true;

    const searchTerm = inputValue.toLowerCase().trim();

    return searchKeys.some((key) => {
      const optionValue = option[key];
      if (typeof optionValue === "string") {
        return optionValue.toLowerCase().includes(searchTerm);
      }
      if (typeof optionValue === "number") {
        return optionValue.toString().includes(searchTerm);
      }
      return false;
    });
  };

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchValue) return options;

    const filterFn = filterOption || defaultFilterOption;
    return options.filter((option) => filterFn(option, searchValue));
  }, [options, searchValue, filterOption, isSearchable]);

  const handleInputChange = (inputValue: string, { action }: any) => {
    if (action === "input-change") {
      setSearchValue(inputValue);
      onInputChange?.(inputValue);
    } else if (action === "menu-close") {
      setSearchValue("");
    }
  };

  const relativeMenu: any = isRelativeMenu ? { position: "relative" } : {};

  return (
    <div className={cn(`relative ${className} ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`)}>
      {label && (
        <label
          className={`
                  absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2] leading-3 pointer-events-none
                  ${error ? "text-error_main" : "text-grey-600"}
                `}
          style={{
            background: isDisabled
              ? "linear-gradient(to bottom, #ffffff 50%, #F4F6F8 50%)"
              : "#ffffff",
          }}
        >
          {label}
          {required && <span className="text-error_main ml-1">*</span>}
        </label>
      )}

      <ReactSelect
        className={`basic-single`}
        classNamePrefix="select"
        defaultValue={defaultValue}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isRtl={isRtl}
        isSearchable={isSearchable}
        name={name}
        value={value}
        options={isSearchable ? filteredOptions : options}
        placeholder={placeholder}
        blurInputOnSelect={true}
        hideSelectedOptions={false}
        closeMenuOnSelect={true}
        onChange={onChange}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={onMenuScrollToBottom}
        inputValue={isSearchable ? searchValue : undefined}
        isOptionDisabled={(option) => option.disabled || false}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "結果が見つかりません" : "選択肢がありません"
        }
        loadingMessage={() => "ローディング中..."}
        styles={{
          control: (base) => ({
            ...base,
            height: height,
            width: width,
            borderRadius: "0.5rem",
            borderColor: error ? "red" : "rgba(145, 158, 171, 0.32)",
            backgroundColor: isDisabled ? "#f4f6f8" : "#ffffff",
            color: "var(--primary-color)",
            boxShadow: "none",
            "&:hover": {
              borderColor: "var(--primary-color)",
            },
          }),
          indicatorSeparator(base) {
            return {
              ...base,
              display: "none",
            };
          },
          dropdownIndicator(base, props) {
            return {
              ...base,
              transform: props.selectProps.menuIsOpen
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            };
          },
          menuList(base) {
            return {
              ...base,
              padding: "0",
              maxHeight: "194px",
            };
          },
          menu(base) {
            return {
              ...base,
              borderRadius: "0.5rem",
              marginTop: "0.25rem",
              overflow: "hidden",
              zIndex: 100,
              ...relativeMenu,
            };
          },
          placeholder(base) {
            return {
              ...base,
              color: "rgb(156 163 175 / 0.8)",
              fontSize: "0.75rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
              whiteSpace: "nowrap",
            };
          },
          input(base) {
            return {
              ...base,
              fontSize: "0.875rem",
              color: "#212B36",
            };
          },
          singleValue(base, props) {
            return {
              ...base,
              fontSize: "0.875rem",
              color: "#212B36",
              opacity: props.selectProps.menuIsOpen && isSearchable ? 0 : 1,
            };
          },
          option(base, props) {
            return {
              ...base,
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              fontWeight: "400",
              lineHeight: "1.375rem",
              color: "#212B36",
              backgroundColor: props.isSelected
                ? "#DFE3E8"
                : props.isFocused
                  ? "#F4F6F8"
                  : "transparent",
              cursor: props.isDisabled ? "not-allowed" : "pointer",
              opacity: props.isDisabled ? 0.5 : 1,
              borderBottom: "1px solid rgba(145, 158, 171, 0.32)",
              "&:last-child": {
                borderBottom: "none",
              },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            };
          },
        }}
      />

      {error && <p className="text-error_main text-xs mt-1">{error}</p>}
    </div>
  );
};
